<template>
  <div class="relative">
    <!-- Title -->
    <div class="title">
      <h1>Reporte de envíos</h1>
      <div class="toolbar">
        <button @click="exportToExcel" class="btn btn--outline">Exportar</button>
      </div>
    </div>

    <!-- Empty / Loading state -->
    <div v-if="!hasFetched" class="text-center py-16 text-gray-500 text-sm">
      Selecciona un rango de fechas y presiona <strong>Filtrar</strong> para generar el reporte.
    </div>
    <div v-else-if="list.length === 0" class="text-center py-16 text-gray-500 text-sm">
      No se encontraron envíos para el rango seleccionado.
    </div>

    <!-- REPORTE DE ENVIOS -->
    <div v-if="list.length > 0" class="max-w-full overflow-auto mb-8">
      <table class="w-full border-collapse text-xs">
        <caption class="bg-[#1F3864] text-white py-2 font-bold text-sm tracking-wide">REPORTE DE ENVIOS</caption>
        <thead>
          <tr class="bg-[#1F3864] text-white text-center">
            <th class="border border-white px-3 py-2 text-left">FECHA</th>
            <th class="border border-white px-3 py-2">TOTAL DE ENVIOS</th>
            <th class="border border-white px-3 py-2">ENTREGADOS</th>
            <th class="border border-white px-3 py-2">REPROGRAMADOS</th>
            <th class="border border-white px-3 py-2">RECHAZADOS</th>
            <th class="border border-white px-3 py-2">RECHAZADOS CON COBRO</th>
            <th class="border border-white px-3 py-2">ANULADOS</th>
            <th class="border border-white px-3 py-2">ANULADOS CON COBRO</th>
            <th class="border border-white px-3 py-2">EFECTIVIDAD DEL SERVICIO (%)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(group, date) in groupedByDate" :key="date" class="border border-gray-300">
            <td class="border border-gray-300 px-3 py-2 font-bold">{{ formatDateDisplay(date) }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ group.length }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'done') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countRescheduled(group) }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'rejected') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'rejectedWithCharge') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'canceled') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ countByStatus(group, 'canceledWithCharge') }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center font-bold"
              :class="calcEfficiency(group) >= 100 ? 'bg-yellow-200 text-yellow-700' : calcEfficiency(group) >= 80 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'">
              {{ calcEfficiency(group) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- REPORTE DE FINANZAS -->
    <div v-if="list.length > 0" class="max-w-full overflow-auto mb-8">
      <table class="w-full border-collapse text-xs">
        <caption class="bg-[#1F3864] text-white py-2 font-bold text-sm tracking-wide">REPORTE DE FINANZAS</caption>
        <thead>
          <tr class="bg-[#1F3864] text-white text-center">
            <th class="border border-white px-3 py-2" rowspan="2" style="vertical-align:middle; text-align:left">FECHA
            </th>
            <th class="border border-white px-3 py-2" colspan="3">TOTAL SERVICIO DE ENWAM (-)</th>
            <th class="border border-white px-3 py-2" colspan="3">TOTAL DINERO DE TIENDA (+)</th>
            <th class="border border-white px-3 py-2" rowspan="2" style="vertical-align:middle">TOTAL (S/.)</th>
            <th class="border border-white px-3 py-2" rowspan="2" style="vertical-align:middle">ESTADO</th>
          </tr>
          <tr class="bg-[#1F3864] text-white text-center">
            <th class="border border-white px-3 py-2">COSTO DE RECOJO<br><span class="font-normal">(s/.10)</span></th>
            <th class="border border-white px-3 py-2">ADICIONALES<br><span class="font-normal">(Tamaño, Peso, otros)
                s/.</span></th>
            <th class="border border-white px-3 py-2">TARIFA TOTAL (s/.)</th>
            <th class="border border-white px-3 py-2">EN CUENTAS ENWAM<br><span class="font-normal">(s/.)</span></th>
            <th class="border border-white px-3 py-2">EN EFECTIVO<br><span class="font-normal">(s/.)</span></th>
            <th class="border border-white px-3 py-2">PAGOS POR SISTEMA<br><span class="font-normal">(s/.)</span></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(group, date) in groupedByDate" :key="date" class="border border-gray-300">
            <td class="border border-gray-300 px-3 py-2 font-bold">{{ formatDateDisplay(date) }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).costoRecojo }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).adicionales }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).tarifaTotal }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).enCuentasEnwam }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).enEfectivo }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center">{{ financeOf(group, date).pagosSistema }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center font-bold"
              :class="parseFloat(financeOf(group, date).total) < 0 ? 'text-[#C00000]' : 'text-green-800'">{{ financeOf(group, date).total }}</td>
            <td class="border border-gray-300 px-3 py-2 text-center font-bold"
              :class="getBalanceStateClass(financeOf(group, date).total)">
              <span>{{ getBalanceStateText(financeOf(group, date).total) }}</span>
              <div v-if="parseFloat(financeOf(group, date).total) < 0" class="mt-1">
                <button v-if="getPendingPayments(date).length === 0"
                  @click="openPaymentModal(date, parseFloat(financeOf(group, date).total))"
                  class="px-2 py-1 text-[10px] rounded bg-[#C00000] text-white hover:opacity-90">
                  Subir voucher
                </button>
                <span v-else class="text-[10px] text-yellow-600 block">Voucher en revisión</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- REPORTE DETALLADO DE ENVIOS -->
    <div v-if="list.length > 0" class="max-w-full overflow-auto mb-8">
      <table class="w-full border-collapse text-xs">
        <caption class="bg-[#1F3864] text-white py-2 font-bold text-sm tracking-wide">REPORTE DETALLADO DE ENVIOS
        </caption>
        <thead>
          <tr class="bg-[#C00000] text-white text-center">
            <th class="border border-white px-2 py-2">COD TRACKING</th>
            <th class="border border-white px-2 py-2">FECHA</th>
            <th class="border border-white px-2 py-2">CLIENTE</th>
            <th class="border border-white px-2 py-2">TIPO DE SERVICIO</th>
            <th class="border border-white px-2 py-2">DISTRITO</th>
            <th class="border border-white px-2 py-2">COSTO DE RECOJO<br><span class="font-normal">(S/.10)</span></th>
            <th class="border border-white px-2 py-2">ADICIONAL O PAGO EN AGENCIA</th>
            <th class="border border-white px-2 py-2">TARIFA</th>
            <th class="border border-white px-2 py-2">TOTAL A COBRAR</th>
            <th class="border border-white px-2 py-2">DETALLE DE COBRO</th>
            <th class="border border-white px-2 py-2">TIPO DE PAGO</th>
            <th class="border border-white px-2 py-2">INGRESO A CUENTAS</th>
            <th class="border border-white px-2 py-2">EFECTIVO</th>
            <th class="border border-white px-2 py-2">ESTADO</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(o, index) in list" :key="index" class="border border-gray-200 hover:bg-gray-50">
            <td class="border border-gray-200 px-2 py-1 whitespace-nowrap">{{ o.correlative }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-nowrap">{{ formatDateDisplay(o.pickUpDate) }}</td>
            <td class="border border-gray-200 px-2 py-1">{{ o.fullName }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-nowrap">{{ o.type.label }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-nowrap">{{ o.district.label }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center">{{ getPickupCostPerShipment(o) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center">{{ o.extraCost > 0 ? o.extraCost : '' }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center whitespace-nowrap">S/.{{ (o.district.price +
              (o.type.price || 0)).toFixed(2) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center">{{ o.totalToBeCollected > 0 ? o.totalToBeCollected
              : '' }}</td>
            <td class="border border-gray-200 px-2 py-1">{{ getChargeDetail(o) }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-nowrap">{{ getPaymentType(o) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center whitespace-nowrap">S/.{{
              getAccountIncome(o).toFixed(2) }}</td>
            <td class="border border-gray-200 px-2 py-1 text-center whitespace-nowrap">S/.{{ getCashAmount(o).toFixed(2)
            }}</td>
            <td class="border border-gray-200 px-2 py-1 whitespace-nowrap">
              <span class="px-2 py-1 rounded text-white text-[10px]" :style="getStatusStyle(o.status)">
                {{ getStatusText(o.status) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Payment voucher modal -->
    <div v-if="showPaymentModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h3 class="text-lg font-bold mb-4">Subir voucher de pago</h3>
        <div class="mb-4">
          <p class="text-sm text-gray-600 mb-1">Fecha de deuda: <strong>{{ formatDateDisplay(paymentData.debtDate) }}</strong></p>
          <p class="text-sm text-gray-600">Monto a pagar: <strong class="text-[#C00000]">S/.{{ Math.abs(paymentData.amount).toFixed(2) }}</strong></p>
        </div>
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">Voucher de pago</label>
          <input type="file" accept="image/*" @change="onVoucherFileChange" class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
          <img v-if="paymentData.previewUrl" :src="paymentData.previewUrl" class="mt-2 max-h-48 rounded border" />
        </div>
        <div class="flex justify-end gap-2">
          <button @click="closePaymentModal" class="px-4 py-2 text-sm rounded border border-gray-300 hover:bg-gray-50">Cancelar</button>
          <button @click="submitPayment" :disabled="!paymentData.file || paymentData.uploading" class="px-4 py-2 text-sm rounded bg-[#1F3864] text-white hover:opacity-90 disabled:opacity-50">
            {{ paymentData.uploading ? 'Enviando...' : 'Enviar voucher' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Filter panel -->
    <div class="filter">
      <div class="filter__trigger" @click="showFilter = !showFilter">
        <svg xmlns="http://www.w3.org/2000/svg" class="ionicon" viewBox="0 0 512 512">
          <title>Filtrar</title>
          <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="32"
            d="M32 144h448M112 256h288M208 368h96" />
        </svg>
      </div>
      <div id="filterContent" class="filter__content" :class="{ 'block opacity-100': showFilter }">
        <div class="form-group mb-4">
          <label>Filtrar por fecha</label>
          <v-date-picker v-model="filterData.range" :dot="true" color="red" is-expanded is-range
            :timezone="`America/Lima`" />
        </div>
        <button @click="fetchData" class="btn">Filtrar</button>
      </div>
    </div>
  </div>
</template>

<script>
import { db, Timestamp, storage } from '@/firebase/firebaseConfig'
import moment from 'moment'

const TIENDA_METHODS = ['Yape Tienda', 'BCP Tienda', 'Interbank Tienda']

export default {
  name: 'DeliveryReport',
  data() {
    return {
      list: [],
      payments: {},
      hasFetched: false,
      showFilter: false,
      showPaymentModal: false,
      paymentData: {
        debtDate: '',
        amount: 0,
        file: null,
        previewUrl: null,
        uploading: false
      },
      filterData: {
        range: {
          start: new Date(),
          end: new Date()
        }
      }
    }
  },
  async created() {
    try {
      await this.$store.dispatch('setUser')
    } catch (e) {
      this.$toast.error('Error al cargar usuario', { duration: 6000 })
    }
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    groupedByDate() {
      const groups = {}
      this.list.forEach(o => {
        const key = o.pickUpDate
        if (!groups[key]) groups[key] = []
        groups[key].push(o)
      })
      // Sort keys
      return Object.keys(groups).sort().reduce((acc, key) => {
        acc[key] = groups[key]
        return acc
      }, {})
    }
  },
  methods: {
    async fetchData() {
      try {
        if (!this.user?.client?.id) return
        this.showFilter = false
        this.$store.dispatch('showProgress')
        this.list = []

        const startDate = new Date(this.filterData.range.start)
        startDate.setHours(0, 0, 0, 0)
        const endDate = new Date(this.filterData.range.end)
        endDate.setHours(23, 59, 59, 0)

        const startTs = Timestamp.fromDate(startDate)
        const endTs = Timestamp.fromDate(endDate)

        const snap = await db.collection('detail')
          .where('clientId', '==', this.user.client.id)
          .where('deadline', '>=', startTs)
          .where('deadline', '<=', endTs)
          .orderBy('deadline', 'asc')
          .get()

        snap.forEach(doc => {
          const data = doc.data()
          const pickUpDate = data.pickUpDate
            ? (typeof data.pickUpDate.toDate === 'function'
              ? moment(data.pickUpDate.toDate()).format('YYYY-MM-DD')
              : data.pickUpDate)
            : ''
          const deadline = data.deadline
            ? (typeof data.deadline.toDate === 'function'
              ? moment(data.deadline.toDate()).format('YYYY-MM-DD')
              : data.deadline)
            : ''

          this.list.push({
            id: doc.id,
            ...data,
            pickUpDate,
            deadline
          })
        })

        // Fetch payments for this client and date range
        await this.fetchPayments()

        this.hasFetched = true
      } catch (e) {
        console.error(e)
        this.$toast.error('Error al cargar reporte', { duration: 6000 })
      } finally {
        this.$store.dispatch('hideProgress')
      }
    },

    async fetchPayments() {
      this.payments = {}
      const clientId = this.user.client.id
      const snap = await db.collection('clients').doc(clientId)
        .collection('payments')
        .where('clientId', '==', clientId)
        .get()

      snap.forEach(doc => {
        const data = doc.data()
        const date = data.debtDate
        if (!this.payments[date]) this.payments[date] = []
        this.payments[date].push({ id: doc.id, ...data })
      })
    },

    getApprovedPayments(date) {
      const datePayments = this.payments[date] || []
      return datePayments.filter(p => p.status === 'approved')
        .reduce((sum, p) => sum + (p.amount || 0), 0)
    },

    getPendingPayments(date) {
      const datePayments = this.payments[date] || []
      return datePayments.filter(p => p.status === 'pending')
    },

    openPaymentModal(date, amount) {
      this.paymentData = {
        debtDate: date,
        amount: Math.abs(amount),
        file: null,
        previewUrl: null,
        uploading: false
      }
      this.showPaymentModal = true
    },

    closePaymentModal() {
      this.showPaymentModal = false
      this.paymentData = { debtDate: '', amount: 0, file: null, previewUrl: null, uploading: false }
    },

    onVoucherFileChange(e) {
      const file = e.target.files[0]
      if (!file) return
      this.paymentData.file = file
      this.paymentData.previewUrl = URL.createObjectURL(file)
    },

    async submitPayment() {
      try {
        this.paymentData.uploading = true
        const clientId = this.user.client.id
        const file = this.paymentData.file
        const fileName = `payments/${clientId}/${Date.now()}_${file.name}`

        // Upload to Storage
        const ref = storage.ref(fileName)
        await ref.put(file)
        const voucherUrl = await ref.getDownloadURL()

        // Save to Firestore subcollection
        await db.collection('clients').doc(clientId)
          .collection('payments').add({
            clientId,
            debtDate: this.paymentData.debtDate,
            amount: this.paymentData.amount,
            voucherUrl,
            status: 'pending',
            createdAt: Timestamp.now()
          })

        this.$toast.success('Voucher enviado correctamente', { duration: 4000 })
        this.closePaymentModal()
        await this.fetchPayments()
      } catch (e) {
        console.error(e)
        this.$toast.error('Error al enviar voucher', { duration: 6000 })
      } finally {
        this.paymentData.uploading = false
      }
    },

    countByStatus(group, status) {
      return group.filter(o => o.status === status).length
    },

    countRescheduled(group) {
      return group.filter(o => ['rescheduledWithoutCharge', 'rescheduledWithCharge'].includes(o.status)).length
    },

    calcEfficiency(group) {
      if (!group.length) return 0
      const delivered = this.countByStatus(group, 'done')
      return Math.round((delivered / group.length) * 100)
    },

    financeOf(group, date) {
      // Costo de recojo only applies when the client had a single shipment that day
      const costoRecojo = group.length === 1 ? 10 : 0
      let adicionales = 0
      let tarifaTotal = 0
      let enCuentasEnwam = 0
      let enEfectivo = 0
      let pagosSistema = 0

      const chargeableStatuses = ['done', 'rescheduledWithCharge', 'canceledWithCharge', 'rejectedWithCharge']

      group.forEach(o => {
        const isChargeable = chargeableStatuses.includes(o.status)

        if (isChargeable) {
          adicionales += o.extraCost || 0
          tarifaTotal += (o.district?.price || 0) + (o.type?.price || 0)
        }

        pagosSistema += (o.district?.price || 0) + (o.type?.price || 0)

        // Breakdown by motive
        const motive = o.dealer?.motive || ''
        const amount = o.totalToBeCollected || 0

        if (motive === 'Pago Mixto') {
          const method1 = o.dealer?.mixedPayment1Method || ''
          const amount1 = o.dealer?.mixedPayment1Amount || 0
          const method2 = o.dealer?.mixedPayment2Method || ''
          const amount2 = o.dealer?.mixedPayment2Amount || 0

          if (!TIENDA_METHODS.includes(method1)) {
            if (method1 === 'Efectivo') enEfectivo += amount1
            else enCuentasEnwam += amount1
          }
          if (!TIENDA_METHODS.includes(method2)) {
            if (method2 === 'Efectivo') enEfectivo += amount2
            else enCuentasEnwam += amount2
          }
        } else if (motive === 'A Enwam') {
          enCuentasEnwam += amount
        } else if (motive === 'Efectivo') {
          enEfectivo += amount
        }
        // 'A Tienda' — no suma, el dinero fue directo a la tienda
      })

      const rawTotal = pagosSistema - tarifaTotal - adicionales - costoRecojo
      const approvedPayments = date ? this.getApprovedPayments(date) : 0
      const total = rawTotal + approvedPayments

      return {
        costoRecojo: costoRecojo.toFixed(1),
        adicionales: adicionales.toFixed(1),
        tarifaTotal: tarifaTotal.toFixed(1),
        enCuentasEnwam: enCuentasEnwam.toFixed(1),
        enEfectivo: enEfectivo.toFixed(1),
        pagosSistema: pagosSistema.toFixed(1),
        rawTotal: rawTotal.toFixed(2),
        total: total.toFixed(2)
      }
    },

    getPickupCostPerShipment(o) {
      // S/.10 only if this shipment is the sole one on its pickup date
      const sameDay = this.list.filter(s => s.pickUpDate === o.pickUpDate)
      return sameDay.length === 1 ? 10 : ''
    },

    getChargeDetail(o) {
      return o.dealer?.motive || '-'
    },

    getPaymentType(o) {
      return o.dealer?.accountDetails || '-'
    },

    getAccountIncome(o) {
      const motive = o.dealer?.motive || ''

      if (motive === 'Pago Mixto') {
        let total = 0
        const method1 = o.dealer?.mixedPayment1Method || ''
        const method2 = o.dealer?.mixedPayment2Method || ''
        if (!TIENDA_METHODS.includes(method1) && method1 !== 'Efectivo') {
          total += o.dealer?.mixedPayment1Amount || 0
        }
        if (!TIENDA_METHODS.includes(method2) && method2 !== 'Efectivo') {
          total += o.dealer?.mixedPayment2Amount || 0
        }
        return total
      }
      if (motive === 'A Enwam') return o.totalToBeCollected || 0
      return 0
    },

    getCashAmount(o) {
      const motive = o.dealer?.motive || ''

      if (motive === 'Pago Mixto') {
        let total = 0
        const method1 = o.dealer?.mixedPayment1Method || ''
        const method2 = o.dealer?.mixedPayment2Method || ''
        if (method1 === 'Efectivo') total += o.dealer?.mixedPayment1Amount || 0
        if (method2 === 'Efectivo') total += o.dealer?.mixedPayment2Amount || 0
        return total
      }
      if (motive !== 'Efectivo') return 0
      const accountDetails = o.dealer?.accountDetails || ''
      if (accountDetails === 'Efectivo' || accountDetails === '') {
        return o.totalToBeCollected || 0
      }
      return 0
    },

    getBalanceStateText(total) {
      const t = parseFloat(total)
      if (t > 0) return 'Por abonar'
      if (t === 0) return 'Sin deuda'
      return 'Debe'
    },

    getBalanceStateClass(total) {
      const t = parseFloat(total)
      if (t > 0) return 'text-green-700'
      if (t === 0) return 'text-gray-500'
      return 'text-[#C00000]'
    },

    getStatusText(status) {
      const map = {
        hold: 'Sin confirmar',
        done: 'Entregado',
        canceled: 'Anulado',
        canceledWithCharge: 'Anulado con cobro',
        rejected: 'Rechazado',
        rejectedWithCharge: 'Rechazado con cobro',
        rescheduledWithoutCharge: 'Reprogramado sin cobro',
        rescheduledWithCharge: 'Reprogramado con cobro'
      }
      return map[status] || status
    },

    getStatusStyle(status) {
      const map = {
        hold: '#9CA3AF',
        done: '#16A34A',
        canceled: '#4B5563',
        canceledWithCharge: '#6B7280',
        rejected: '#DC2626',
        rejectedWithCharge: '#B91C1C',
        rescheduledWithoutCharge: '#EAB308',
        rescheduledWithCharge: '#F97316'
      }
      return { backgroundColor: map[status] || '#9CA3AF' }
    },

    formatDateDisplay(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr, 'YYYY-MM-DD').format('DD/MM/YYYY')
    },

    async exportToExcel() {
      try {
        // Hoja 1: REPORTE DE ENVIOS
        const sheet1Header = ['FECHA', 'TOTAL DE ENVIOS', 'ENTREGADOS', 'REPROGRAMADOS', 'RECHAZADOS', 'RECHAZADOS CON COBRO', 'ANULADOS', 'ANULADOS CON COBRO', 'EFECTIVIDAD DEL SERVICIO (%)']
        const sheet1Data = Object.entries(this.groupedByDate).map(([date, group]) => [
          this.formatDateDisplay(date),
          group.length,
          this.countByStatus(group, 'done'),
          this.countRescheduled(group),
          this.countByStatus(group, 'rejected'),
          this.countByStatus(group, 'rejectedWithCharge'),
          this.countByStatus(group, 'canceled'),
          this.countByStatus(group, 'canceledWithCharge'),
          this.calcEfficiency(group)
        ])

        // Hoja 2: REPORTE DE FINANZAS
        const sheet2Header = ['FECHA', 'COSTO DE RECOJO (s/.10)', 'ADICIONALES (s/.)', 'TARIFA TOTAL (s/.)', 'EN CUENTAS ENWAM (s/.)', 'EN EFECTIVO (s/.)', 'PAGOS POR SISTEMA (s/.)', 'TOTAL (S/.)', 'ESTADO']
        const sheet2Data = Object.entries(this.groupedByDate).map(([date, group]) => {
          const f = this.financeOf(group, date)
          return [
            this.formatDateDisplay(date),
            f.costoRecojo,
            f.adicionales,
            f.tarifaTotal,
            f.enCuentasEnwam,
            f.enEfectivo,
            f.pagosSistema,
            f.total,
            this.getBalanceStateText(f.total)
          ]
        })

        // Hoja 3: REPORTE DETALLADO DE ENVIOS
        const sheet3Header = ['COD TRACKING', 'FECHA', 'CLIENTE', 'TIPO DE SERVICIO', 'DISTRITO', 'COSTO DE RECOJO', 'ADICIONAL O PAGO EN AGENCIA', 'TARIFA', 'TOTAL A COBRAR', 'DETALLE DE COBRO', 'TIPO DE PAGO', 'INGRESO A CUENTAS', 'EFECTIVO', 'ESTADO']
        const sheet3Data = this.list.map(o => [
          o.correlative,
          this.formatDateDisplay(o.pickUpDate),
          o.fullName,
          o.type?.label || '',
          o.district?.label || '',
          this.getPickupCostPerShipment(o),
          o.extraCost > 0 ? o.extraCost : '',
          'S/.' + ((o.district?.price || 0) + (o.type?.price || 0)).toFixed(2),
          o.totalToBeCollected > 0 ? o.totalToBeCollected : '',
          this.getChargeDetail(o),
          this.getPaymentType(o),
          'S/.' + this.getAccountIncome(o).toFixed(2),
          'S/.' + this.getCashAmount(o).toFixed(2),
          this.getStatusText(o.status)
        ])

        import('@/vendor/Export2Excel').then(excel => {
          excel.export_json_to_excel_multi_sheet({
            sheets: [
              { sheetName: 'Reporte de Envios', header: sheet1Header, data: sheet1Data },
              { sheetName: 'Reporte de Finanzas', header: sheet2Header, data: sheet2Data },
              { sheetName: 'Reporte Detallado', header: sheet3Header, data: sheet3Data }
            ],
            filename: 'Reporte de envíos Enwam',
            bookType: 'xlsx'
          })
        })
      } catch (e) {
        console.error(e)
      }
    }
  }
}
</script>
